import config from 'isomorphic-config'

import type { ViewedHotelSelectionApiResponse } from '@/components/TripActivity/types'
import { viewedHotelQuery } from '@/constants/queryFragments'
import analytics from '@/shared-utils/analytics'
import hasGraphErrors from '@/shared-utils/graph-utils'
import isAbortSignalTimeoutSupported from '@/shared-utils/is-abortSignal-supported'
import type { GraphResponse } from '@/types'

import { jsonContentTypeHeader } from '../server/constants'
import getAuthorizationToken from './get-authorization-token'

type ViewedItemsQueryResult = {
  readonly getViewedItemsByCguid: ReadonlyArray<ViewedHotelSelectionApiResponse>
}

type ViewedItemsGraphResponse = GraphResponse<ViewedItemsQueryResult>

function hasRedeemablePayload(payload: unknown): payload is ViewedItemsGraphResponse {
  if (payload && typeof payload === 'object') {
    if ('data' in payload && payload.data && typeof payload.data === 'object') {
      return 'getViewedItemsByCguid' in payload.data && Array.isArray(payload.data.getViewedItemsByCguid)
    }
  }
  return false
}

function isValidGraphQLResponse(payload: unknown): payload is ViewedItemsGraphResponse {
  if (payload && typeof payload === 'object') {
    if ('errors' in payload && Array.isArray(payload.errors)) {
      return true
    }
    if (
      'data' in payload &&
      payload.data &&
      typeof payload.data === 'object' &&
      'getViewedItemsByCguid' in payload.data &&
      (payload.data.getViewedItemsByCguid === null || Array.isArray(payload.data.getViewedItemsByCguid))
    ) {
      return true
    }
  }
  return false
}

const { url, timeout } = config.client['pcln-graph']

type FetchViewedItemsParams = {
  cguid: string
  appName: string
  appVersion: string
  signedIn?: boolean
}

async function fetchViewedItems({ cguid, appName, appVersion, signedIn = false }: FetchViewedItemsParams) {
  const options = {
    method: 'POST',
    signal: isAbortSignalTimeoutSupported() ? AbortSignal.timeout(timeout) : undefined,
    headers: {
      ...jsonContentTypeHeader,
      'apollographql-client-name': appName,
      'apollographql-client-version': appVersion,
      ...(signedIn ? { Authorization: getAuthorizationToken() } : {}),
    },
    body: JSON.stringify({
      query: `
        query ViewedItems($cguid: String!) {
          getViewedItemsByCguid(cguid: $cguid) {
            ${viewedHotelQuery}
          }
        }
      `,
      variables: {
        cguid,
      },
    }),
  }

  const requestUrl = `${url}?gqlOp=getViewedItemsByCguid`

  try {
    const response = await fetch(requestUrl, options)
    const { status, url: urlValue } = response

    // Non-2xx: log and bail — no payload to parse.
    if (!response.ok) {
      analytics.logError({
        message: 'The getViewedItems graph query response status is > 299',
        url: urlValue,
        status,
      })
      return []
    }

    const payload = await response.json()

    // Unexpected shape: log and bail.
    if (!isValidGraphQLResponse(payload)) {
      analytics.logError({
        message: 'The getViewedItems graph query response is not as per contract',
        payload: JSON.stringify(payload),
      })
      return []
    }

    const payloadHasGraphErrors = hasGraphErrors<ReadonlyArray<ViewedHotelSelectionApiResponse>>(payload)
    const payloadHasRedeemableData = hasRedeemablePayload(payload)

    // Partial GraphQL errors are common; still try to use redeemable data below.
    if (payloadHasGraphErrors) {
      analytics.logError({
        message: 'The getViewedItems graph query response has returned errors.',
        errorMessage: payload.errors?.[0]?.message || 'Unknown error',
        url: requestUrl,
        appName,
        appVersion,
        errorObjectAsString: JSON.stringify(payload.errors),
        hasRedeemablePayload: payloadHasRedeemableData,
      })
    }

    // Return items when the response is clean, or when errors coexist with usable data.
    if (payload.data && (!payloadHasGraphErrors || payloadHasRedeemableData)) {
      return 'getViewedItemsByCguid' in payload.data ? payload.data.getViewedItemsByCguid : []
    }

    return []
  } catch (error) {
    const errorMessage =
      error instanceof Error ? `${error.message} Error Name: ${error.name}` : 'Unknown error'
    analytics.logError({
      message: 'Failed to call the getViewedItems graph query.',
      errorMessage,
      error: JSON.stringify(error),
    })
    return []
  }
}

export { fetchViewedItems }
